/**
 * Turning an asset record into a form, and a form back into a PUT body.
 *
 * Extracted from `AssetEdit.tsx` for the reason `verify-outcome.ts` was
 * extracted from `Channels.tsx`: a React handler is the one place in this
 * codebase nothing can reach, and what these functions decide is not small.
 *
 * ── 🔴 Why any of this needs care ────────────────────────────────────────
 *
 * `PUT /api/assets/:id` is a FULL REPLACE. It writes `site_id`,
 * `parent_asset_id`, `kind`, `driver_id`, `credential_id`, `name`, `code`,
 * `lifecycle`, `config` and `notes` from the body, and every one of those that
 * the body omits becomes NULL.
 *
 * Every test in `assets_api.test.ts` drives that route with a hand-written body
 * naming every field. A form does not get that luxury — it has to reconstruct
 * the whole record from what it was handed, and anything it fails to carry
 * across is destroyed silently on the first save of an unrelated field.
 *
 * Which is why the pane loads `GET /api/assets/:id` (`ASSET_COLUMNS`, the same
 * set the UPDATE writes) rather than reusing the `/api/assets/:id/detail`
 * payload the drawer already has. The detail route has no `credential_id`.
 * `asset_edit_screen.test.ts` pins the UPDATE's column list against
 * `ASSET_COLUMNS` so that pairing cannot rot.
 */

/**
 * The lifecycles the server accepts. Mirrors LIFECYCLES in src/assets/asset-edit.ts.
 *
 * 🔴 A value missing here is worse than a missing menu entry. The select is
 * driven by `value={draft.lifecycle}`, so an asset whose lifecycle is not in this
 * list renders with nothing selected while the draft still holds it — and saving
 * sends it to a server that refuses it, so the screen reports a lifecycle error
 * for an edit that was changing the name. That is what an off-hire asset did
 * until 2026-09-18. `asset_lifecycles.test.ts` pins this list against the server's
 * and against migration 020's CHECK constraint, because nothing else can.
 */
export const LIFECYCLES = ['provisioning', 'active', 'maintenance', 'off_hire', 'retired'] as const;

/**
 * The keys the collector will dial, in the order it tries them.
 *
 * Mirrors `ADDRESS_KEYS` in `src/assets/address.ts`. Here only so the address
 * box can say which key it writes — the RULE about needing one is the server's
 * and is deliberately not repeated in the browser.
 */
export const ADDRESS_KEYS = ['host', 'vpn_ip', 'ip'] as const;

/** The full record, as `ASSET_COLUMNS` returns it. */
export interface AssetRecord {
  id: string;
  site_id: string | null;
  parent_asset_id: string | null;
  kind: string;
  driver_id: string | null;
  credential_id: string | null;
  name: string;
  code: string | null;
  lifecycle: string;
  config: Record<string, unknown> | null;
  notes: string | null;
  retired_at: string | null;
  /**
   * The two exemptions, which are read here and written through their OWN
   * routes — never through the asset PUT, which rewrites every column.
   *
   * ⚠️ Optional because an `AssetRecord` built by hand elsewhere will not carry
   * them. Absent must read as "not exempt", never as "exempt with no reason".
   */
  records_onboard?: boolean;
  onboard_exempt_reason?: string | null;
  encoding_exempt_reason?: string | null;
  /** 073: a single-drive host, NX recording to the system drive on purpose. */
  system_drive_records?: boolean;
}

/** The form, as strings — which is what inputs give and the API accepts. */
export interface Draft {
  name: string;
  code: string;
  kind: string;
  driverId: string;
  siteId: string;
  credentialId: string;
  lifecycle: string;
  notes: string;
  /** Which of ADDRESS_KEYS this asset uses. Preserved rather than normalised. */
  addressKey: string;
  address: string;
  port: string;
  /** Everything in config that is not the address or the port, as JSON. */
  extraConfig: string;
}

/** Raised when the extra-config box does not hold an object. */
export class ConfigTextError extends Error {}

/**
 * Which address key this asset actually carries.
 *
 * ⚠️ Preserved rather than normalised to `host`. The imported fleet uses all
 * three — `assets_api.test.ts` has cases for `vpn_ip` and `ip` precisely because
 * the legacy import wrote both — and silently rewriting a camera's `vpn_ip` into
 * `host` on an unrelated save would be this screen editing a field nobody
 * touched.
 */
export function addressKeyOf(config: Record<string, unknown>): string {
  for (const key of ADDRESS_KEYS) {
    const value = config[key];
    if (typeof value === 'string' && value.trim() !== '') return key;
  }
  return 'host';
}

const EXTRA_SKIP = new Set<string>([...ADDRESS_KEYS, 'port']);

function extraConfigOf(config: Record<string, unknown>): string {
  const rest = Object.fromEntries(Object.entries(config).filter(([k]) => !EXTRA_SKIP.has(k)));
  return Object.keys(rest).length === 0 ? '' : JSON.stringify(rest, null, 2);
}

export function draftOf(asset: AssetRecord): Draft {
  const config = asset.config ?? {};
  const addressKey = addressKeyOf(config);
  const rawPort = config['port'];
  return {
    name: asset.name,
    code: asset.code ?? '',
    kind: asset.kind,
    driverId: asset.driver_id ?? '',
    siteId: asset.site_id ?? '',
    credentialId: asset.credential_id ?? '',
    lifecycle: asset.lifecycle,
    notes: asset.notes ?? '',
    addressKey,
    address: typeof config[addressKey] === 'string' ? (config[addressKey] as string) : '',
    port: rawPort === undefined || rawPort === null ? '' : String(rawPort),
    extraConfig: extraConfigOf(config),
  };
}

/**
 * Rebuild `config` from the form.
 *
 * 🔴 The extra-config box is parsed and MERGED, not sent as the whole config.
 * Sending it whole would drop the address and port the two boxes above own, and
 * an asset with no address is not broken — it is INVISIBLE. The collector leaves
 * it, it records nothing, and the fleet shows a camera that reads as quiet
 * rather than as never asked. That is the failure the server's
 * active-needs-an-address rule exists to prevent, reintroduced one layer up
 * where that rule cannot see it.
 *
 * ⚠️ The port is sent as TYPED, not coerced here. `validateAssetEdit` coerces a
 * numeric string deliberately — "an HTML form posts 8000, which is not a number"
 * is the case its own comment describes — and refuses a port that is not one.
 * Coercing in the browser would take that refusal away from the only layer that
 * can enforce it.
 */
export function configFrom(draft: Draft): Record<string, unknown> {
  let extra: Record<string, unknown> = {};
  const text = draft.extraConfig.trim();
  if (text !== '') {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      throw new ConfigTextError('the extra config is not valid JSON');
    }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new ConfigTextError('the extra config must be a JSON object');
    }
    extra = parsed as Record<string, unknown>;
  }

  const config: Record<string, unknown> = { ...extra };
  const address = draft.address.trim();
  /*
   * An empty address REMOVES the key rather than writing "". `deviceAddress`
   * treats an empty string as absent — "a form posts an empty box" is its own
   * comment — so writing one would leave a key that reads as an address to a
   * person and as nothing to the collector.
   */
  if (address !== '') config[draft.addressKey] = address;
  const port = draft.port.trim();
  if (port !== '') config['port'] = port;
  return config;
}

export function bodyFrom(draft: Draft): Record<string, unknown> {
  return {
    name: draft.name,
    code: draft.code === '' ? null : draft.code,
    kind: draft.kind,
    driverId: draft.driverId === '' ? null : draft.driverId,
    siteId: draft.siteId === '' ? null : draft.siteId,
    credentialId: draft.credentialId === '' ? null : draft.credentialId,
    lifecycle: draft.lifecycle,
    notes: draft.notes === '' ? null : draft.notes,
    config: configFrom(draft),
  };
}

/**
 * Drivers the collector never dials: every capability pushed by an agent on
 * the device. An `active` asset on one needs no address, so the forms do not
 * ask for one. The server decides the same thing from the driver catalogue
 * (`isPushOnlyDriver` in src/assets/asset-edit.ts); this copy exists because the
 * bundle cannot import the catalogue, and test/asset_address_rule.test.ts fails
 * the moment the two disagree.
 */
export const PUSH_ONLY_DRIVERS: readonly string[] = ['cosmon-agent', 'cosmon-agent-nvr', 'cosmon-agent-desktop', 'cosmon-agent-laptop', 'nx-camera'];
